import React, { useState } from 'react';
import { ImageDown, Loader2 } from 'lucide-react';
import oshoLogo from '../assets/osho-logo.png';

const loadImg = (src, cross = true) => new Promise((resolve, reject) => {
  const img = new Image();
  if (cross) img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const wrapText = (ctx, text, maxWidth) => {
  const words = (text || '').split(' ');
  const lines = [];
  let line = '';
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  });
  if (line) lines.push(line);
  return lines;
};

// Generates a 1080x1920 Instagram/TikTok-story graphic and downloads it.
const StoryShareImage = ({ title, subtitle = '', coverUrl }) => {
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const W = 1080, H = 1920;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');

      // base warm background
      ctx.fillStyle = '#F3E4DE'; ctx.fillRect(0, 0, W, H);

      // cover photo (cover-fit) — falls back to gradient if it can't be loaded CORS-safe
      let hasCover = false;
      if (coverUrl) {
        try {
          const img = await loadImg(coverUrl, true);
          const ir = img.width / img.height, cr = W / H;
          let dw, dh, dx, dy;
          if (ir > cr) { dh = H; dw = H * ir; dx = (W - dw) / 2; dy = 0; }
          else { dw = W; dh = W / ir; dx = 0; dy = (H - dh) / 2; }
          ctx.drawImage(img, dx, dy, dw, dh);
          hasCover = true;
        } catch { hasCover = false; }
      }
      if (!hasCover) {
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, '#7B2D3A'); g.addColorStop(1, '#3A1620');
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }

      // dark gradient scrim (top + bottom) for legibility
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, 'rgba(30,10,15,0.55)');
      grad.addColorStop(0.45, 'rgba(30,10,15,0.15)');
      grad.addColorStop(0.75, 'rgba(30,10,15,0.55)');
      grad.addColorStop(1, 'rgba(30,10,15,0.9)');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

      // logo top
      try {
        const logo = await loadImg(oshoLogo, false);
        const lw = 300, lh = lw * (logo.height / logo.width);
        ctx.drawImage(logo, (W - lw) / 2, 90, lw, lh);
      } catch { /* skip */ }

      // eyebrow
      ctx.textAlign = 'center';
      ctx.fillStyle = '#F3C9B8';
      ctx.font = '600 34px Georgia, serif';
      ctx.fillText("YOU'RE INVITED", W / 2, H - 620);

      // title
      ctx.fillStyle = '#FFF7F2';
      ctx.font = 'bold 84px Georgia, serif';
      const lines = wrapText(ctx, title, W - 160).slice(0, 4);
      let y = H - 520;
      lines.forEach((ln) => { ctx.fillText(ln, W / 2, y); y += 100; });

      // subtitle (date / location)
      if (subtitle) {
        ctx.fillStyle = '#EED8CE';
        ctx.font = '44px Georgia, serif';
        wrapText(ctx, subtitle, W - 200).slice(0, 2).forEach((ln) => { ctx.fillText(ln, W / 2, y + 20); y += 58; });
      }

      // footer CTA
      ctx.fillStyle = '#F3C9B8';
      ctx.font = '600 40px Georgia, serif';
      ctx.fillText('Book now · oshouniversal.org', W / 2, H - 110);

      const a = document.createElement('a');
      try {
        a.href = canvas.toDataURL('image/png');
      } catch {
        // Cover image tainted the canvas (no CORS) — redraw gradient-only and export.
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, '#7B2D3A'); g.addColorStop(1, '#3A1620');
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
        try { const logo = await loadImg(oshoLogo, false); const lw = 300, lh = lw * (logo.height / logo.width); ctx.drawImage(logo, (W - lw) / 2, 90, lw, lh); } catch { /* skip */ }
        ctx.textAlign = 'center';
        ctx.fillStyle = '#F3C9B8'; ctx.font = '600 34px Georgia, serif'; ctx.fillText("YOU'RE INVITED", W / 2, H - 620);
        ctx.fillStyle = '#FFF7F2'; ctx.font = 'bold 84px Georgia, serif';
        let yy = H - 520;
        wrapText(ctx, title, W - 160).slice(0, 4).forEach((ln) => { ctx.fillText(ln, W / 2, yy); yy += 100; });
        if (subtitle) { ctx.fillStyle = '#EED8CE'; ctx.font = '44px Georgia, serif'; wrapText(ctx, subtitle, W - 200).slice(0, 2).forEach((ln) => { ctx.fillText(ln, W / 2, yy + 20); yy += 58; }); }
        ctx.fillStyle = '#F3C9B8'; ctx.font = '600 40px Georgia, serif'; ctx.fillText('Book now · oshouniversal.org', W / 2, H - 110);
        a.href = canvas.toDataURL('image/png');
      }
      a.download = `${(title || 'osho-event').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-story.png`;
      a.click();
    } finally { setBusy(false); }
  };

  return (
    <button onClick={generate} disabled={busy} className="pill-btn-outline" data-testid="download-story-image">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />} Story image
    </button>
  );
};

export default StoryShareImage;
